import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { SurveyAnswer, SurveyAssessment } from '@/features/assessments'
import {
  SurveyErrorBoundary,
  SurveyForm,
  SurveyFormSkeleton,
  useSurveyFormState,
} from '@/features/assessments'
import {
  useConvexSurveyAssessment,
  useConvexSubmitAnswers,
  useConvexCompleteResponse,
  useConvexSavedAnswers,
  useConvexSaveCheckpoint,
} from '@/features/assessments/services/assessments.convex'
import {
  useMyConvexUserId,
  useCurrentOrganizationId,
  useOrganizationData,
} from '@/features/auth'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'

type SurveySearch = {
  page?: number
}

export const Route = createFileRoute(
  '/(dashboard)/assessments/$assessmentId/responses/$id',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SurveySearch => {
    return {
      page: search.page !== undefined ? Number(search.page) : undefined,
    }
  },
  beforeLoad: ({ params, location }) => {
    // Check if we came from DirectReports route by looking at the state
    const isFromDirectReports =
      (location.state as { from?: string } | undefined)?.from ===
      'Direct Reports'

    return {
      breadcrumbs: isFromDirectReports
        ? [
            { title: 'Assessments', href: `/assessments` },
            { title: 'Direct Reports', href: `/assessments/direct-reports` },
            { title: params.assessmentId },
            { title: 'Responses' },
          ]
        : [
            { title: 'Assessments', href: `/assessments` },
            { title: params.assessmentId },
            { title: 'Responses' },
          ],
    }
  },
})

// ── Loading wrapper ──────────────────────────────────────────────────────────

/**
 * RouteComponent is a thin loading wrapper.
 *
 * It waits for BOTH the assessment data AND server-saved checkpoint answers
 * before rendering SurveyContent. This is important because useSurveyFormState
 * uses useState which only reads its initialiser once — we must have the server
 * answers available before the first render of the form.
 */
function RouteComponent() {
  const { assessmentId, id: assessmentResponseId } = Route.useParams()

  const raw = useConvexSurveyAssessment(assessmentId)

  // Load any server-persisted checkpoint answers so the user can resume
  // mid-survey across devices or after clearing local storage.
  const savedAnswersRaw = useConvexSavedAnswers(assessmentResponseId)

  // Both queries must resolve before we render the form.
  if (raw === undefined || savedAnswersRaw === undefined) {
    return <SurveyFormSkeleton />
  }

  if (raw === null) {
    return (
      <div className="max-w-4xl mx-auto p-4 bg-card rounded-lg border">
        <p>Assessment not found</p>
      </div>
    )
  }

  // Convert server answers array to Record<questionId, answerValue>
  const serverAnswers: Record<string, number> = {}
  for (const { questionId, answerValue } of savedAnswersRaw) {
    serverAnswers[questionId] = answerValue
  }

  return (
    <SurveyContent
      raw={raw}
      serverAnswers={serverAnswers}
      assessmentResponseId={assessmentResponseId}
    />
  )
}

// ── Main survey form ─────────────────────────────────────────────────────────

type RawSurveyAssessment = NonNullable<
  ReturnType<typeof useConvexSurveyAssessment>
>

interface SurveyContentProps {
  raw: RawSurveyAssessment
  serverAnswers: Record<string, number>
  assessmentResponseId: string
}

/**
 * Renders the actual survey form once all data is available.
 * Separated so that useSurveyFormState's useState initialises with the definite
 * serverAnswers value (not undefined/loading).
 */
function SurveyContent({
  raw,
  serverAnswers,
  assessmentResponseId,
}: SurveyContentProps) {
  const { assessmentId } = Route.useParams()
  const { page } = Route.useSearch()

  const navigate = useNavigate()
  const currentUserId = useMyConvexUserId()
  const currentOrgId = useCurrentOrganizationId()
  const organizations = useOrganizationData()
  const { showConfirmDialog } = useConfirmDialog()

  const submitSurveyMutation = useConvexSubmitAnswers()
  const completeAssessmentMutation = useConvexCompleteResponse()
  const saveCheckpointFn = useConvexSaveCheckpoint()

  // Stable checkpoint callback — saves current answers + page to server on page navigation
  const onSaveCheckpoint = useCallback(
    (answers: Record<string, number>, page: number) => {
      saveCheckpointFn(assessmentResponseId, answers, page)
    },
    [saveCheckpointFn, assessmentResponseId],
  )

  // Adapt Convex survey shape for useSurveyFormState (questions need `id`)
  const assessment = useMemo(
    () => ({
      ...raw,
      id: raw._id,
      employeeId: raw.employeeUserId,
      managerId: raw.managerUserId,
      responses: raw.responses.map((r) => ({
        ...r,
        id: r._id,
        completedAt: r.completedAt
          ? new Date(r.completedAt).toISOString()
          : null,
      })),
      assessmentType: raw.assessmentType
        ? {
            ...raw.assessmentType,
            id: raw.assessmentType._id,
            questions:
              raw.assessmentType.questions?.map((q) => ({
                ...q,
                id: q._id,
              })) ?? [],
          }
        : undefined,
    }),
    [raw],
  )

  const {
    currentPage,
    answers,
    handlePageChange,
    handleAnswerChange,
    clearProgress,
  } = useSurveyFormState({
    assessmentId,
    assessmentResponseId,
    urlPage: page,
    questions: assessment.assessmentType?.questions ?? [],
    serverAnswers,
    onSaveCheckpoint,
  })

  const isEmployee = useMemo(
    () => currentUserId === assessment.employeeId,
    [currentUserId, assessment.employeeId],
  )

  const organizationName = useMemo(() => {
    const currentOrg = organizations.find((org) => org.id === currentOrgId)
    return currentOrg?.name ?? 'the organization'
  }, [organizations, currentOrgId])

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Authorization check — redirect if user is not the employee or manager
  useEffect(() => {
    if (!currentUserId) return
    const userIsEmployee = assessment.employeeId === currentUserId
    const userIsManager = assessment.managerId === currentUserId

    if (!userIsEmployee && !userIsManager) {
      void navigate({ to: '/assessments', replace: true })
      return
    }

    const responseExists = (assessment.responses ?? []).some(
      (r) => r.id === assessmentResponseId,
    )
    if (!responseExists) {
      void navigate({ to: '/assessments', replace: true })
    }
  }, [assessment, currentUserId, navigate, assessmentResponseId])

  const handleSurveySubmit = useCallback(() => {
    showConfirmDialog({
      title: 'Submit Assessment',
      description:
        'Are you sure you want to submit this assessment? Your answers will be finalized and cannot be changed after submission.',
      confirmText: 'Submit',
      cancelText: 'Review',
      variant: 'default',
      onConfirm: async () => {
        setIsSubmitting(true)
        try {
          // Submit all answers (includes current page which was not yet checkpointed)
          const surveyAnswers: Array<SurveyAnswer> = Object.entries(
            answers,
          ).map(([questionId, answerValue]) => ({ questionId, answerValue }))

          await submitSurveyMutation.mutateAsync({
            assessmentResponseId:
              assessmentResponseId as import('@backend/_generated/dataModel').Id<'assessmentResponses'>,
            payload: { answers: surveyAnswers },
          })

          // Mark complete — triggers calculateAssessmentResponseAverage (server-side)
          await completeAssessmentMutation.mutateAsync(
            assessmentResponseId as import('@backend/_generated/dataModel').Id<'assessmentResponses'>,
          )

          clearProgress()

          if (isEmployee) {
            await navigate({ to: '/assessments', replace: true })
          } else {
            await navigate({ to: '/assessments/direct-reports', replace: true })
          }
        } finally {
          setIsSubmitting(false)
        }
      },
    })
  }, [
    showConfirmDialog,
    answers,
    assessmentResponseId,
    submitSurveyMutation,
    completeAssessmentMutation,
    clearProgress,
    isEmployee,
    navigate,
  ])

  return (
    <SurveyErrorBoundary onReset={clearProgress}>
      <SurveyForm
        // Convex shape is structurally compatible at runtime; the legacy
        // REST-API fields (assessmentTypeId, periodId, status) are never
        // read by SurveyForm — it only uses assessmentType.questions and responses.
        assessment={assessment as unknown as SurveyAssessment}
        currentPage={currentPage}
        answers={answers}
        isEmployee={isEmployee}
        organizationName={organizationName}
        isSubmitting={isSubmitting}
        onPageChange={handlePageChange}
        onAnswerChange={handleAnswerChange}
        onSubmit={handleSurveySubmit}
      />
    </SurveyErrorBoundary>
  )
}
